import { ConfigService } from '@axo/config/ConfigService';
import { reloadData } from '@axo/form/data-access/backend-sync';
import { Product, Purpose } from '@axo/shared/data-access/types';
import { AnalyticsEvent } from '@axo/shared/services/analytics';

export type GetAnalyticsData = {
  product?: Product;
  customerId?: string;
  step?: number;
  formData?: Record<string, unknown>;
  action?: 'Viewed' | 'Initiated' | 'Completed';
};

export function getAnalyticsData(args: GetAnalyticsData): AnalyticsEvent {
  const { product, formData, step, action, customerId } = args;

  const { env } = ConfigService.get('env');
  const { brand, country } = ConfigService.get('global');

  const { applicationId } = reloadData.get();

  const variant = product === Product.CreditCard ? 'CC' : undefined;

  const tags = {
    feature: 'Form',
    variant,
    step: !!step && `Step ${step}`,
    action,
  } as const;

  const event = Object.values(tags)
    .filter((tag) => !!tag)
    .join(' ');

  const params = {
    brand,
    env,
    product,
    country,
    customerId,
  };

  if (Object.keys(formData ?? {}).length > 0) {
    Object.assign(params, {
      loanApplicationID: applicationId,
      appliedAmount: formData?.['LoanApplication/AppliedAmount'],
      loanDuration: formData?.['LoanApplication/LoanDuration'],
      purpose: formData?.['LoanApplication/Purpose'],
    });
  }

  if (formData?.['LoanApplication/InitialPurpose']) {
    Object.assign(params, {
      initialPurpose: formData?.['LoanApplication/InitialPurpose'],
      didApplyForRefinancing:
        formData?.['LoanApplication/InitialPurpose'] ===
        Purpose.RefinancingLoan,
    });
  }

  if (formData?.['LoanApplication/TotalRefinancingAmount']) {
    Object.assign(params, {
      totalRefinancingAmount:
        formData?.['LoanApplication/TotalRefinancingAmount'],
    });
  }

  if (action === 'Completed') {
    Object.assign(params, {
      formCompletedTimestamp: new Date().toISOString(),
    });
  }

  const options: AnalyticsEvent['options'] = {
    send_immediately: action === 'Completed',
  };

  return {
    event,
    params,
    options,
  } as AnalyticsEvent;
}
